import { type SearchRequest } from '@searchkit/api';
import { merge } from 'merge-anything';

import { type SearchRuntimeSortingConfig } from '~/apps/product-plus/search-domain/types';
import { mergeQueries, toSortSelectionValue } from '~/apps/product-plus/search-domain/utils';

type ExtendedSearchRequest = SearchRequest & {
  facet?: string;
  type?: 'facet';
};

export function createQueryFactory(config: {
  resultAttributes: string[];
  searchRequest: SearchRequest;
  sorting: SearchRuntimeSortingConfig;
}) {
  const { resultAttributes, searchRequest, sorting } = config;
  const request = searchRequest as ExtendedSearchRequest;

  const { hitsPerPage = 10 } = request.request?.params ?? {};
  const defaultSort = sorting.options[0];
  if (!defaultSort) {
    throw new Error('Default sort not found');
  }

  const activeSort =
    sorting.options.find(sort => `${request.indexName}${toSortSelectionValue(sort)}` === request.request.indexName) ??
    defaultSort;

  const numericFields = new Set([
    'oddsDecimal',
    'eventId',
    'marketMovement.swingPercentage',
    'horseRacingSportsData.distance.miles',
    'horseRacingSportsData.distance.furlongs',
  ]);
  const getUnmappedType = (field: string) => (numericFields.has(field) ? 'double' : undefined);

  const sortConfig = {
    [activeSort.field]: {
      order: activeSort.order,
      ...(getUnmappedType(activeSort.field) && { unmapped_type: getUnmappedType(activeSort.field) }),
    },
  };

  const existingAggs = request.body?.aggs || {};
  const aggKeys = Object.keys(existingAggs);
  const isFacetRequest =
    aggKeys.length === 1 &&
    (aggKeys[0].includes('marketDisplayNameMap') || aggKeys[0].includes('horseRacingSportsData'));

  const aggregations = !isFacetRequest
    ? {
        odds_by_bet: {
          terms: { field: 'betId', size: 1000 },
        },
        unique_bets: {
          cardinality: { field: 'betId' },
        },
      }
    : undefined;

  const collapse = !isFacetRequest
    ? {
        field: 'betId',
        inner_hits: [
          {
            _source: { includes: resultAttributes },
            name: 'best_odds',
            size: 1,
            sort: [sortConfig, { oddsDecimal: 'desc' }],
          },
          {
            _source: { includes: ['betId', 'oddsDecimal', 'oddsAmerican', 'oddsFractional'] },
            name: 'shortest_odds',
            size: 1,
            sort: [{ oddsDecimal: 'asc' }],
          },
        ],
      }
    : undefined;

  const swingPercentageFilter = {
    bool: {
      must_not: [
        {
          term: {
            'marketMovement.swingPercentage': 0,
          },
        },
      ],
    },
  };

  return merge(request, {
    body: {
      ...(aggregations && { aggs: aggregations }),
      ...(collapse && { collapse }),
      query: mergeQueries(request.body?.query ?? {}, swingPercentageFilter),
      size: hitsPerPage,
      sort: [sortConfig],
    },
  }) as SearchRequest;
}
